import { zipSync, strToU8 } from "fflate";

/* Writing a real .xlsx, without a megabyte of library to do it.
 *
 * An .xlsx is a zip of XML parts. The app already carries fflate to read Word
 * and Excel files, so writing one costs a few hundred lines rather than a
 * dependency that would roughly double a bundle already flagged as large.
 *
 * Deliberately narrow. Numbers are numbers, dates are dates, everything else
 * is text, and that is the whole type system. No formulas, no merged cells, no
 * charts. What it does carry is the three things that decide whether a person
 * can work in the file the moment it opens: the header row frozen, a filter on
 * every column, and columns wide enough to read.
 *
 * Strings go inline rather than through a shared string table. A shared table
 * is smaller on a file with heavy repetition and is another moving part to get
 * wrong; at the sizes here, 555 schedule rows, the difference is not worth it.
 */

const esc = (s) => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  /* Excel refuses control characters outright, and they turn up in text
     pasted out of PDFs and email. Dropped rather than escaped. */
  .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");

/* Excel counts days from 1900 but believes 1900 was a leap year, so the epoch
   that makes the arithmetic come out right is 30 December 1899. */
const EPOCH = Date.UTC(1899, 11, 30);
const DAY = 86400000;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function serialFor(iso) {
  if (typeof iso !== "string" || !ISO.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const at = Date.UTC(y, m - 1, d);
  const back = new Date(at);
  /* Rejects 31 February and friends, which roll forward silently otherwise. */
  if (back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round((at - EPOCH) / DAY);
}

/* A1, B1 ... Z1, AA1. */
export function colName(index) {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/* Style slots defined in styles.xml below: 0 plain, 1 bold header, 2 date. */
const PLAIN = 0, HEAD = 1, DATE = 2;

function cell(ref, value) {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${value}</v></c>`;
  }
  if (typeof value === "boolean") {
    return `<c r="${ref}" t="b"><v>${value ? 1 : 0}</v></c>`;
  }
  const serial = serialFor(value);
  if (serial !== null) {
    return `<c r="${ref}" s="${DATE}"><v>${serial}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${esc(value)}</t></is></c>`;
}

function widthOf(header, rows, key) {
  let widest = String(header ?? "").length;
  for (const row of rows) {
    const v = row?.[key];
    if (v === null || v === undefined) continue;
    const len = String(v).length;
    if (len > widest) widest = len;
    /* Long notes would otherwise produce a column half a screen wide. */
    if (widest > 60) return 60;
  }
  return Math.min(Math.max(widest + 2, 9), 60);
}

function sheetXml({ columns, rows }) {
  const last = colName(columns.length - 1);
  const extent = `A1:${last}${rows.length + 1}`;

  const head = columns
    .map((c, at) => `<c r="${colName(at)}1" t="inlineStr" s="${HEAD}"><is><t>${esc(c.label)}</t></is></c>`)
    .join("");

  const body = rows.map((row, at) => {
    const r = at + 2;
    const cells = columns.map((c, i) => cell(`${colName(i)}${r}`, row[c.key])).join("");
    return `<row r="${r}">${cells}</row>`;
  }).join("");

  const cols = columns
    .map((c, at) => `<col min="${at + 1}" max="${at + 1}" width="${widthOf(c.label, rows, c.key)}" customWidth="1"/>`)
    .join("");

  /* Element order is enforced by the schema: sheetViews, then cols, then
     sheetData, then autoFilter. Out of order and Excel calls the file corrupt
     rather than saying which part it disliked. */
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
    + `<dimension ref="${extent}"/>`
    + `<sheetViews><sheetView workbookViewId="0">`
    + `<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>`
    + `</sheetView></sheetViews>`
    + `<sheetFormatPr defaultRowHeight="15"/>`
    + (cols ? `<cols>${cols}</cols>` : "")
    + `<sheetData><row r="1">${head}</row>${body}</sheetData>`
    + `<autoFilter ref="${extent}"/>`
    + `</worksheet>`;
}

/* Excel allows 31 characters and forbids : \ / ? * [ ], and two sheets cannot
   share a name even by accident, so collisions are numbered rather than
   silently dropped. */
export function sheetNames(titles) {
  const used = new Set();
  return titles.map((raw, at) => {
    let name = String(raw ?? "").replace(/[:\\/?*[\]]/g, " ").trim().slice(0, 31) || `Sheet${at + 1}`;
    if (!used.has(name.toLowerCase())) { used.add(name.toLowerCase()); return name; }
    for (let n = 2; ; n += 1) {
      const tail = ` (${n})`;
      const tried = `${name.slice(0, 31 - tail.length)}${tail}`;
      if (!used.has(tried.toLowerCase())) { used.add(tried.toLowerCase()); return tried; }
    }
  });
}

const STYLES = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
  + `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">`
  + `<numFmts count="1"><numFmt numFmtId="164" formatCode="yyyy\\-mm\\-dd"/></numFmts>`
  + `<fonts count="2">`
  + `<font><sz val="11"/><name val="Calibri"/></font>`
  + `<font><b/><sz val="11"/><name val="Calibri"/></font>`
  + `</fonts>`
  + `<fills count="2"><fill><patternFill patternType="none"/></fill>`
  + `<fill><patternFill patternType="gray125"/></fill></fills>`
  + `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>`
  + `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>`
  + `<cellXfs count="3">`
  + `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`
  + `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>`
  + `<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>`
  + `</cellXfs>`
  + `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>`
  + `</styleSheet>`;

/**
 * sheets: [{ title, columns: [{key, label}], rows: [object] }]
 * returns a Uint8Array holding a complete .xlsx
 */
export function buildWorkbook(sheets) {
  if (!sheets?.length) throw new Error("A workbook needs at least one sheet.");

  const names = sheetNames(sheets.map((s) => s.title));

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"`
    + ` xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">`
    + `<sheets>${names.map((n, at) =>
        `<sheet name="${esc(n)}" sheetId="${at + 1}" r:id="rId${at + 1}"/>`).join("")}</sheets>`
    + `</workbook>`;

  const wbRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + names.map((_, at) =>
        `<Relationship Id="rId${at + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${at + 1}.xml"/>`).join("")
    + `<Relationship Id="rId${names.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`
    + `</Relationships>`;

  const types = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
    + `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>`
    + `<Default Extension="xml" ContentType="application/xml"/>`
    + `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>`
    + names.map((_, at) =>
        `<Override PartName="/xl/worksheets/sheet${at + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("")
    + `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>`
    + `</Types>`;

  const root = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>`
    + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">`
    + `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>`
    + `</Relationships>`;

  const files = {
    "[Content_Types].xml": strToU8(types),
    "_rels/.rels": strToU8(root),
    "xl/workbook.xml": strToU8(workbook),
    "xl/_rels/workbook.xml.rels": strToU8(wbRels),
    "xl/styles.xml": strToU8(STYLES),
  };
  sheets.forEach((s, at) => {
    files[`xl/worksheets/sheet${at + 1}.xml`] = strToU8(sheetXml(s));
  });

  return zipSync(files, { level: 6 });
}
